import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Breadcrumbs } from "@/components/breadcrumbs";

// 096 -- Portal-hosted mock of the provider OAuth flow. The real Elegant
// Fuels experience will replace this page with their own hosted form --
// the /shop/:slug/link endpoint hands out the redirect URL, so flipping
// between mock and real is a server-side toggle.
export default function ShopLinkMockPage() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const state = params.get("state") ?? "";
  const [accountRef, setAccountRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!slug || !state || !accountRef.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.completeProviderLink(slug, state, accountRef.trim());
      navigate("/me/cashback", { replace: true });
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to complete linking");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-page="shop-link-mock" className="flex flex-col gap-6">
      <Breadcrumbs items={[{ label: "Events", to: "/discover" }, { label: "Shop", to: "/shop" }, { label: "Link account" }]} />
      <h1 className="text-2xl font-semibold">Link your account</h1>

      <Card>
        <CardHeader>
          <CardTitle>Account details</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Enter your card number (or account reference) to complete linking. Your spend will start earning cashback from the next transaction.
          </p>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="account_ref">Card number</Label>
              <Input
                id="account_ref"
                required
                value={accountRef}
                onChange={(e) => setAccountRef(e.target.value)}
                placeholder="e.g. 1234 5678 9012 3456"
                data-field="external_account_ref"
              />
            </div>
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting || !state || !accountRef.trim()} data-action="complete-link">
                {submitting ? "Linking…" : "Link account"}
              </Button>
              <Link to={`/shop/${slug}`} className={buttonVariants({ variant: "outline" })}>Cancel</Link>
            </div>
          </form>
          {!state && (
            <p className="text-xs text-destructive">Missing link state -- start the link flow from the shop page.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
